import { Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  PublicMusicthonState,
  MusicProviderItem,
  PublicMusicthonConfig,
} from "./model/musicthon-state.model";
import { PublicAckRequest } from "./dto/public-ack.request";
import { environment } from "../../../environments/environment";

@Injectable({ providedIn: "root" })
export class MusicthonService {
  private readonly currentSignal =
    signal<PublicMusicthonState["current"]>(null);
  private readonly queuedSignal = signal<PublicMusicthonState["queued"]>([]);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly playlistsSignal = signal<MusicProviderItem[]>([]);
  private readonly devicesSignal = signal<MusicProviderItem[]>([]);
  private readonly publicConfigSignal = signal<PublicMusicthonConfig | null>(
    null
  );

  constructor(private readonly http: HttpClient) {}

  current() {
    return this.currentSignal;
  }

  queued() {
    return this.queuedSignal;
  }

  loading() {
    return this.loadingSignal;
  }

  error() {
    return this.errorSignal;
  }

  playlists() {
    return this.playlistsSignal;
  }

  devices() {
    return this.devicesSignal;
  }

  publicConfig() {
    return this.publicConfigSignal;
  }

  setPublicConfig(config: PublicMusicthonConfig | null) {
    this.publicConfigSignal.set(config);
  }

  async loadCurrent(streamerHash: string): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const resp = await this.http
        .get<PublicMusicthonState>(
          `${environment.backend}/musicthon/current/${streamerHash}`
        )
        .toPromise();

      this.currentSignal.set(resp?.current ?? null);
      this.queuedSignal.set(resp?.queued ?? []);
    } catch (e: any) {
      this.errorSignal.set(e?.message ?? "load_failed");
      this.currentSignal.set(null);
      this.queuedSignal.set([]);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async loadPublicConfig(streamerHash: string): Promise<PublicMusicthonConfig | null> {
    try {
      const resp = await this.http
        .get<PublicMusicthonConfig>(
          `${environment.backend}/musicthon/public/config/${streamerHash}`
        )
        .toPromise();
      this.publicConfigSignal.set(resp ?? null);
      return resp ?? null;
    } catch (e: any) {
      this.publicConfigSignal.set(null);
      return null;
    }
  }

  async loadPlaylists(provider: string): Promise<MusicProviderItem[]> {
    try {
      const resp = await this.http
        .get<{ items: MusicProviderItem[] }>(
          `${environment.backend}/musicthon/providers/${provider}/playlists`
        )
        .toPromise();
      const items = resp?.items ?? [];
      this.playlistsSignal.set(items);
      return items;
    } catch {
      this.playlistsSignal.set([]);
      return [];
    }
  }

  async loadDevices(provider: string): Promise<MusicProviderItem[]> {
    try {
      const resp = await this.http
        .get<{ items: MusicProviderItem[] }>(
          `${environment.backend}/musicthon/providers/${provider}/devices`
        )
        .toPromise();
      const items = resp?.items ?? [];
      this.devicesSignal.set(items);
      return items;
    } catch {
      this.devicesSignal.set([]);
      return [];
    }
  }

  async publicAck(request: PublicAckRequest): Promise<void> {
    await this.http
      .post<{ success: boolean }>(
        `${environment.backend}/musicthon/public/ack`,
        request
      )
      .toPromise();
  }
}
